// 1:1 port from age-of-mcp v3 footbridge_head — plank-and-post, flat at the
// contract height: a stone abutment block under a bearer cap carrying a
// checkered plank landing (four alternating light/dark plank squares,
// rotation-symmetric so the deck reads as planking from either span direction)
// with a capped timber newel post at each corner. Every plank top sits at the
// fixed walk height.
import { defineModel, styleV3, type Part } from "../../lib";

const { STONE, STONE_DK, WOOD, WOOD_DK, box, poles, slab } = styleV3;

const WALK_TOP = 0.42;        // the renderer's HEAD_TOP for this type — exact
const PLANK_T  = 0.03;        // plank thickness

// The stone abutment mass at height `h`, with its darker bearer cap.
function abutment(h: number): Part[] {
  return [
    box(0.72, h, 0.72, STONE, [0, h / 2, 0]),
    box(0.66, 0.04, 0.66, STONE_DK, [0, h + 0.02, 0]),
  ];
}

// The plank landing: a 2x2 checker of plank squares whose tops are the walk
// surface — alternating shades, rotation-symmetric, reading as boards from
// any side.
function landing(): Part[] {
  const out: Part[] = [];
  for (const x of [-0.16, 0.16]) {
    for (const z of [-0.16, 0.16]) {
      const light = x * z > 0;   // diagonal pairs share a shade — a checker
      out.push(box(0.31, PLANK_T, 0.31, light ? WOOD : WOOD_DK,
        [x, WALK_TOP - PLANK_T / 2, z]));
    }
  }
  return out;
}

// A capped newel post at each corner — identical on all four, the railing
// terminal whichever way the span runs.
function newels(): Part[] {
  const out: Part[] = [];
  for (const x of [-0.31, 0.31]) {
    for (const z of [-0.31, 0.31]) {
      out.push(
        box(0.07, 0.2, 0.07, WOOD_DK, [x, WALK_TOP + 0.1, z]),
        box(0.09, 0.03, 0.09, STONE_DK, [x, WALK_TOP + 0.215, z]),
      );
    }
  }
  return out;
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(0.78, 0.78, undefined, 0.08)];
  if (s === 1) return [...abutment(0.18)];
  if (s === 2) return [...abutment(0.34), ...poles(0.7, 0.7, 0.5)];
  return [
    ...abutment(WALK_TOP - PLANK_T - 0.04),
    ...landing(),
    ...newels(),
  ];
}

export const footbridgeHeadV3 = defineModel({
  id: "footbridge-head-v3",
  tiles: { w: 1, h: 1 },
  batchId: "45-optimized",
  name: "Footbridge head (v3)",
  tags: ["building","footbridge","head","low-poly","age-of-mcp","v3","water"],
  summary: "Low-poly footbridge head.",
  description: "A ramp onto a footbridge; a pair on opposite banks spans a gap with a plank walkway.",
  author: "age-of-mcp",
  footprint: 0.42,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
